import { useEffect, useMemo } from 'react';

import { ModalV2, SkeletonLoader } from '@equip.health/ui';

import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { GroupClassEventOption } from '~/lib/constants/scheduling.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { isUserAttendingEvent } from '~/lib/util/classes/groupClasses.util';
import { getGroupClassEventOptions } from '~/lib/util/schedule/schedule.util';
import { useGetGroupClassEventMeetingDetailsMutation } from '~/store/services/groupClass';
import {
  useGetGroupClassNotesByEventExternalIdsQuery,
  useGetGroupSectionNotesByEventExternalIdsQuery,
} from '~/store/services/notes/notes';

import { isBaseQueryError } from '~/lib/types/api/isQueryError';
import EventDayCard from '~/components/common/calendar/schedule/day-view/EventDayCard';
import { useIsPatientPath } from '~/lib/hooks/useIsPatientPath';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { useFlags } from 'launchdarkly-react-client-sdk';
export interface GroupClassEventDetailsModalProps {
  groupClassEventExternalId: string;
  id: string;
  isOpen: boolean;
  onClose: () => void;
  onRemoveEvent: (
    groupClassEventExternalId: string,
    isRemoveEvent?: boolean,
  ) => void;
  scheduleItem: ScheduleItemBase;
  title: string;
}

const GroupClassEventDetailsModal = ({
  groupClassEventExternalId,
  id,
  isOpen,
  onClose,
  onRemoveEvent,
  scheduleItem,
  title,
}: GroupClassEventDetailsModalProps) => {
  const isPatientPath = useIsPatientPath();
  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();
  const [getMeetingDetails] = useGetGroupClassEventMeetingDetailsMutation();
  const {
    data: groupClassNotesData,
    isLoading: isGroupClassNotesLoading,
    error: groupClassNotesError,
  } = useGetGroupClassNotesByEventExternalIdsQuery(
    [groupClassEventExternalId],
    {
      skip: isPatientPath || !isOpen || isGroupsRestructuringEnabled,
    },
  );

  const {
    data: groupSectionNotesData,
    isLoading: isGroupSectionNotesLoading,
    error: groupSectionNotesError,
  } = useGetGroupSectionNotesByEventExternalIdsQuery(
    [groupClassEventExternalId],
    {
      skip: isPatientPath || !isOpen || !isGroupsRestructuringEnabled,
    },
  );

  const groupClassNotes = isGroupsRestructuringEnabled
    ? groupSectionNotesData
    : groupClassNotesData;
  const isNotesLoading = isGroupsRestructuringEnabled
    ? isGroupSectionNotesLoading
    : isGroupClassNotesLoading;
  const notesError = isGroupsRestructuringEnabled
    ? groupSectionNotesError
    : groupClassNotesError;
  const { logError } = useDatadog();

  const { setComponentVisibility } = useComponentPortalContext();

  const {
    currentUser: { userId: userExternalId },
    permissions,
  } = useGlobalContext();

  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (notesError && isBaseQueryError(notesError)) {
      logError(new Error(JSON.stringify(notesError)));
      showErrorToast(
        GlobalErrorCode.GET_GROUP_CLASS_EVENT_NOTES,
        500,
        notesError.data?.toString(),
      );
    }
  }, [notesError]);

  const onOpenZoom = (groupClassEventExternalId: string): void => {
    getMeetingDetails(groupClassEventExternalId)
      .unwrap()
      .then(({ data: meetingDetails }) => {
        if (meetingDetails) {
          window.open(
            meetingDetails.joinUrl ?? meetingDetails.startUrl,
            '_blank',
            'noopener',
          );
        }
      })
      .catch((error) => {
        showErrorToast(
          GlobalErrorCode.GET_GROUP_CLASS_EVENT_MEETING_DETAILS,
          error.status || 500,
          error.data || 'Unknown error',
        );
      });
  };

  const hostIds =
    scheduleItem?.hosts.map(
      (host: GroupClassEventAttendee) => host.externalId,
    ) ?? [];

  const isHost = hostIds.includes(userExternalId);
  const isAttendee = isUserAttendingEvent(
    scheduleItem?.attendees,
    userExternalId,
  );
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];
  const isNoteSigned = scheduleItem?.isNoteInSignedState;

  const isAddNoteDisabled = useMemo<boolean>(
    () =>
      getGroupClassEventOptions(isHost, isAttendee, isSchedulingAdmin)[
        GroupClassEventOption.ADD_NOTE
      ].isDisabled,
    [isHost, isAttendee, isSchedulingAdmin],
  );

  const onNoteClicked = (): void => {
    if (!isNoteSigned && isAddNoteDisabled) {
      // If a note is signed, any user can view it, but only the host can add a new note.
      return;
    }

    onClose();

    setComponentVisibility({
      component: PortalComponent.NOTE_ACCORDION,
      data: {
        existingNoteId: groupClassNotes[0]?.noteExternalId,
        existingNoteStatus: groupClassNotes[0]?.noteStatus,
        groupClassEventExternalId,
        groupClassEventTitle: scheduleItem?.title,
      },
      isExpanded: true,
      isVisible: true,
    });
  };

  return (
    <ModalV2 id={id} isOpen={isOpen} onClose={onClose} size="5xl" title={title}>
      <SkeletonLoader id={id} isLoaded={!isNotesLoading}>
        <EventDayCard
          onCancelAppointment={onRemoveEvent}
          onNoteButtonClick={onNoteClicked}
          onZoomClick={() => onOpenZoom(groupClassEventExternalId)}
          scheduleItem={scheduleItem}
          shouldCloseModal={onClose}
        />
      </SkeletonLoader>
    </ModalV2>
  );
};
export default GroupClassEventDetailsModal;
